"use client";

import { Phone, Mail, Clock, MapPin } from "lucide-react";

/**
 * Contact Section Component
 * Provides the clinic's location (via Google Maps), contact details, and working hours.
 * Includes interactive links for phone and email, and a responsive layout.
 * Each card now uses its own accent color (rose / gold / navy) instead of a
 * single flat navy tint everywhere, for a more colorful aesthetic-clinic feel.
 */
export function Contact() {
  return (
    <section
      id="contact"
      className="aesthetic-gradient py-12 lg:py-16 scroll-mt-20"
      aria-labelledby="contact-title"
    >
      <div className="container mx-auto px-4">
        {/* Section Header: Title and call to action */}
        <div className="mb-12 text-center">
          <p className="text-rose mb-2 font-semibold">Get in Touch</p>
          <h2
            id="contact-title"
            className="mb-4 text-3xl font-bold md:text-4xl"
          >
            Contact Us
          </h2>
          <p className="text-muted-foreground mx-auto max-w-lg text-lg">
            Visit our clinic or reach out to us for any inquiries.
          </p>
        </div>

        <div className="grid items-stretch gap-8 md:grid-cols-2">
          {/* Interactive Map: Embedded Google Maps for location finding */}
          <div className="relative h-full min-h-[400px] overflow-hidden rounded-2xl shadow-lg">
            <iframe
              src="https://maps.google.com/maps?q=SkinSio%20Vadodara%20A/203%20The%20Atlantic%20Makarpura%20Road%20Vadodara&t=m&z=17&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="eager"
              referrerPolicy="no-referrer-when-downgrade"
              title="SkinSio Vadodara Clinic Location Map"
            />
          </div>

          {/* Contact Information and Working Hours */}
          <div className="flex flex-col gap-6">
            {/* Address Card — rose accent */}
            <div className="bg-background border-rose/20 hover-lift group flex flex-col items-center rounded-2xl border p-6 text-center shadow-sm">
              <div className="bg-rose/10 text-rose group-hover:bg-rose mb-4 rounded-2xl p-4 transition-colors duration-300 group-hover:text-white">
                <MapPin className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="mb-2 text-lg font-bold">Visit Us</h3>
              <p className="text-muted-foreground group-hover:text-rose max-w-md font-medium transition-colors">
                2nd Floor, The Atlantic, A/203, Makarpura Rd, beside Indian Oil
                Petrol Pump, near D-Mart, Manjalpur, Vadodara, Gujarat 390010
              </p>
            </div>

            {/* Quick Contact Cards: Phone (gold) and Email (navy) */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Phone Contact Card — gold accent */}
              <a
                href="tel:+919322022392"
                className="bg-background border-golden/30 hover-lift group flex cursor-pointer flex-col items-center rounded-2xl border p-6 text-center shadow-sm"
                aria-label="Call SkinSio Vadodara at +91 9322022392"
              >
                <div className="bg-golden/10 text-golden group-hover:bg-golden mb-4 rounded-2xl p-4 transition-colors duration-300 group-hover:text-white">
                  <Phone className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mb-2 text-lg font-bold">Call Us</h3>
                <span className="text-muted-foreground group-hover:text-golden font-medium transition-colors">
                  +91 9322022392
                </span>
              </a>

              {/* Email Contact Card — navy accent */}
              <a
                href="mailto:care@skinsioclinics.com"
                className="bg-background border-primary/20 hover-lift group flex cursor-pointer flex-col items-center rounded-2xl border p-6 text-center shadow-sm"
                aria-label="Email SkinSio Vadodara at care@skinsioclinics.com"
              >
                <div className="bg-primary/10 text-primary group-hover:bg-primary mb-4 rounded-2xl p-4 transition-colors duration-300 group-hover:text-white">
                  <Mail className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mb-2 text-lg font-bold">Email Us</h3>
                <span className="text-muted-foreground group-hover:text-primary font-medium break-all transition-colors">
                  care@skinsioclinics.com
                </span>
              </a>
            </div>

            {/* Working Hours Card: Clearly displays clinic availability */}
            <div className="bg-background border-rose/20 hover-lift group relative overflow-hidden rounded-2xl border p-8 shadow-sm">
              <div className="mb-6 flex items-center justify-center gap-4 md:justify-start">
                <div className="bg-rose/10 text-rose rounded-xl p-3">
                  <Clock className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold">Working Hours</h3>
              </div>

              <div className="space-y-4">
                {/* Weekday Hours */}
                <div className="bg-rose/5 border-rose/10 flex flex-col items-center justify-between rounded-xl border p-3 lg:flex-row">
                  <span className="text-muted-foreground font-medium">
                    Monday - Saturday
                  </span>
                  <span className="text-rose font-bold">
                    10:30 AM - 07:00 PM
                  </span>
                </div>
                {/* Sunday Hours */}
                <div className="bg-golden/5 border-golden/10 flex flex-col items-center justify-between rounded-xl border p-3 lg:flex-row">
                  <span className="text-muted-foreground font-medium">
                    Sunday
                  </span>
                  <span className="text-golden font-bold">
                    10:30 AM - 03:00 PM
                  </span>
                </div>
                {/* Appointment Policy Note */}
                <div className="text-rose flex items-center justify-center gap-2 pt-2 text-center text-sm font-semibold italic md:text-left">
                  <div
                    className="bg-rose h-2 w-2 animate-pulse rounded-full"
                    aria-hidden="true"
                  />
                  Appointments Required*
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
